import { useState, useEffect } from "react";
import React from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaTrashAlt, FaPlus } from "react-icons/fa";
import "../css/ViewDeliveryAddress.css"; // import the CSS (path may vary)

/**
 * ViewDeliveryAddress - nicer card-based delivery address view
 * - reads addresses from sessionStorage.active-user
 * - deletes via API and updates sessionStorage & local state (no full reload)
 */
const ViewDeliveryAddress = () => {
  const customer_jwtToken = sessionStorage.getItem("customer-jwtToken");
  const sessionUser = JSON.parse(sessionStorage.getItem("active-user")) || {};
  const [addresses, setAddresses] = useState(
    sessionUser.deliveryAddresses || []
  );
  const [deletingId, setDeletingId] = useState(null);

  // keep local addresses in sync if sessionStorage changes externally
  useEffect(() => {
    const onStorage = () => {
      const u = JSON.parse(sessionStorage.getItem("active-user")) || {};
      setAddresses(u.deliveryAddresses || []);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const deleteAddress = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?"))
      return;

    try {
      setDeletingId(addressId);
      const res = await fetch(
        `https://goldenhides.in/api/user/delivery/address/delete?addressId=${addressId}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + customer_jwtToken,
          },
        }
      );

      const body = await res.json();
      if (body.success) {
        toast.success(body.responseMessage || "Address deleted", {
          position: "top-center",
          autoClose: 1200,
        });

        // Update sessionStorage with returned user (if present) OR remove locally
        if (body.users && body.users[0]) {
          sessionStorage.setItem("active-user", JSON.stringify(body.users[0]));
          setAddresses(body.users[0].deliveryAddresses || []);
        } else {
          // fallback: remove from local state
          const filtered = addresses.filter((a) => a.id !== addressId);
          setAddresses(filtered);
          // also update sessionStorage.active-user if present
          const u = JSON.parse(sessionStorage.getItem("active-user")) || {};
          if (u.deliveryAddresses) {
            u.deliveryAddresses = filtered;
            sessionStorage.setItem("active-user", JSON.stringify(u));
          }
        }
      } else {
        toast.error(body.responseMessage || "Failed to delete address", {
          position: "top-center",
          autoClose: 1500,
        });
      }
    } catch (err) {
      console.error("deleteAddress error", err);
      toast.error("It seems server is down", {
        position: "top-center",
        autoClose: 1500,
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="view-address-page container-fluid px-0">
      {/* Header */}
      <div className="address-header d-flex align-items-center justify-content-between px-4">
        <div>
          <h2 className="mb-0">Delivery Addresses</h2>
          <div className="small text-muted">
            Manage your saved delivery addresses
          </div>
        </div>

        <div>
          <a
            href="/delivery/address/add"
            className="btn add-address-btn d-inline-flex align-items-center"
          >
            <FaPlus className="me-2" /> Add Address
          </a>
        </div>
      </div>

      <div className="py-4 px-4">
        {addresses.length === 0 ? (
          <div className="text-center py-5 bg-white rounded shadow-sm">
            <p className="mb-2 lead">No delivery addresses yet</p>
            <a className="btn btn-outline-success" href="/delivery/address/add">
              Add your first address
            </a>
          </div>
        ) : (
          <div className="row g-3">
            {addresses.map((address) => (
              <div key={address.id} className="col-12 col-sm-6 col-lg-4">
                <div className="address-card p-3 h-100 shadow-sm">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <div>
                      <div className="address-title">{address.addressLine}</div>
                      <div className="address-sub small text-muted">
                        {address.downtown} · {address.city}
                      </div>
                    </div>

                    <div className="ms-2 text-end">
                      <span
                        className={`badge status-badge ${
                          address.status === "Active"
                            ? "badge-active"
                            : "badge-inactive"
                        }`}
                      >
                        {address.status}
                      </span>
                    </div>
                  </div>

                  <hr className="my-2" />

                  <div className="mb-3">
                    <div className="small text-muted">Latitude</div>
                    <div className="coord-value text-muted">
                      {address.latitude ?? "-"}
                    </div>

                    <div className="small text-muted mt-2">Longitude</div>
                    <div className="coord-value text-muted">
                      {address.longitude ?? "-"}
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    {address.status === "Active" ? (
                      <button
                        className="btn btn-light btn-sm delete-icon-btn"
                        onClick={() => deleteAddress(address.id)}
                        disabled={deletingId === address.id}
                        title="Delete address"
                      >
                        <FaTrashAlt />{" "}
                        <span className="visually-hidden">Delete</span>
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        disabled
                      >
                        Cannot delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default ViewDeliveryAddress;
